import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Search, Lock, BadgeCheck, ArrowRight, Store, Loader2 } from "lucide-react";
import { api, fmt } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/shop/ProductCard";
import { ShinyButton } from "@/components/ui/ShinyButton";

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "savings", label: "Biggest savings" },
  { id: "price_asc", label: "Price: low to high" },
  { id: "price_desc", label: "Price: high to low" },
  { id: "name", label: "Name A–Z" },
];

const PAGE_SIZE = 12;

export default function PrimeStorePage() {
  const navigate = useNavigate();
  const { user, prime, primeActive } = useStore();
  const [products, setProducts] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("featured");
  const seq = useRef(0);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    api.get("/prime/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/prime/store/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async (pageNum, append) => {
    const mySeq = ++seq.current;
    if (append) setLoadingMore(true);
    else setProducts(null);
    try {
      const params = new URLSearchParams({ page: String(pageNum), limit: String(PAGE_SIZE) });
      if (q.trim()) params.set("q", q.trim());
      if (category) params.set("category", category);
      if (sort !== "featured") params.set("sort", sort);
      const { data } = await api.get(`/prime/store/products?${params}`);
      if (mySeq !== seq.current) return; // stale response — a newer request owns the grid
      setTotal(data.total || 0);
      setHasMore(Boolean(data.has_more));
      setPage(pageNum);
      setProducts((prev) => (append && prev ? [...prev, ...(data.products || [])] : (data.products || [])));
    } catch {
      if (mySeq === seq.current && !append) setProducts([]);
    } finally {
      if (mySeq === seq.current) setLoadingMore(false);
    }
  }, [q, category, sort]);

  // debounced refetch on filter changes
  useEffect(() => {
    const t = setTimeout(() => fetchProducts(1, false), 250);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  // infinite scroll sentinel
  useEffect(() => {
    if (!hasMore || loadingMore || products === null) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) fetchProducts(page + 1, true);
    }, { rootMargin: "600px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, products, page, fetchProducts]);

  const expired = user && prime?.state === "expired";
  const memberSince = useMemo(() => prime?.membership?.expires_at, [prime]);

  return (
    <div className="min-h-screen bg-base pb-20" data-testid="prime-store-page">
      {/* Hero band */}
      <section className="relative overflow-hidden bg-[#242521] pb-14 pt-24 text-center">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(245,158,11,0.14),transparent_60%)]" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#E4B66D]/40 bg-[#E4B66D]/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-[#E4B66D]">
            <Crown className="h-3.5 w-3.5" /> Members only pricing
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
            PRIME <span className="text-[#E4B66D]">STORE</span>
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm text-neutral-400">
            A hand-picked catalogue with member-only prices. Same products, same MOQs — lower rates for PRIME members.
          </p>
        </motion.div>
      </section>

      {/* Membership state banner */}
      <section className="mx-auto mt-6 max-w-7xl px-4 sm:px-6">
        {primeActive ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.07] px-5 py-4" data-testid="prime-store-member-banner">
            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
              <BadgeCheck className="h-4.5 h-5 w-5 shrink-0" /> PRIME active — you're seeing member prices
              {memberSince && <span className="text-xs font-medium text-emerald-600/70 dark:text-emerald-500/70">· valid till {new Date(memberSince).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>}
            </p>
            <button onClick={() => navigate("/account/prime")} className="text-xs font-bold text-emerald-600 underline underline-offset-4 dark:text-emerald-400" data-testid="prime-store-manage-link">Manage membership</button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E4B66D]/45 bg-[#E4B66D]/[0.10] px-5 py-4" data-testid="prime-store-join-banner">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#8A6A2F]">
              <Lock className="h-4 w-4 shrink-0" />
              {expired
                ? "Your PRIME membership has expired — renew to unlock these prices again."
                : <>You're browsing regular prices. Join PRIME{cfg ? ` for ${fmt(cfg.price)}` : ""} to unlock member prices on every product here.</>}
            </p>
            <ShinyButton
              data-testid="prime-store-join-btn"
              onClick={() => navigate(user ? "/prime" : "/login?redirect=/prime")}
              variant="prime"
              className="rounded-full"
              contentClassName="flex items-center gap-2 px-5 py-2 text-xs font-extrabold"
            >
              {expired ? "Renew PRIME" : user ? "Join PRIME" : "Sign in to join"} <ArrowRight className="h-3.5 w-3.5" />
            </ShinyButton>
          </div>
        )}

        {/* Filters row: search + sort (categories live in the sidebar on desktop, chips on mobile) */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search the PRIME Store…"
              className="h-10 w-64 rounded-full border border-line bg-surface pl-9 pr-4 text-sm text-1 placeholder:text-neutral-500 focus:border-[#C89A4B]/60 focus:outline-none"
              data-testid="prime-store-search"
            />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="prime-store-sort"
            className="ml-auto h-10 rounded-full border border-line bg-surface px-4 text-xs font-semibold text-1 focus:border-[#C89A4B]/60 focus:outline-none">
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>

        {/* category chips — mobile only (desktop uses the sidebar) */}
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1 lg:hidden" data-lenis-prevent>
          <button onClick={() => setCategory("")} data-testid="prime-store-cat-all"
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${!category ? "bg-[#242521] text-[#E4B66D]" : "border border-line bg-surface text-2 hover:border-[#242521]/40"}`}>
            All
          </button>
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c === category ? "" : c)} data-testid={`prime-store-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${category === c ? "bg-[#242521] text-[#E4B66D]" : "border border-line bg-surface text-2 hover:border-[#242521]/40"}`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Catalogue: sidebar + paginated grid (same pattern as the main shop) */}
      <section className="mx-auto mt-8 flex max-w-7xl gap-8 px-4 sm:px-6">
        <aside className="sticky top-24 hidden w-52 shrink-0 self-start lg:block" data-testid="prime-store-sidebar">
          <p className="px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-3">Categories</p>
          <nav className="mt-3 space-y-0.5">
            <button onClick={() => setCategory("")} data-testid="prime-store-cat-all-side"
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-left text-sm font-semibold transition-colors ${!category ? "bg-[#242521] text-[#E4B66D]" : "text-2 hover:bg-hover"}`}>
              All products
              <span className="text-[10px] font-bold opacity-60">{total || ""}</span>
            </button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c === category ? "" : c)} data-testid={`prime-store-cat-side-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex w-full items-center rounded-xl px-3.5 py-2.5 text-left text-sm font-semibold transition-colors ${category === c ? "bg-[#242521] text-[#E4B66D]" : "text-2 hover:bg-hover"}`}>
                {c}
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 flex-1">
        {products === null ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-xl border border-line bg-surface" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="mx-auto max-w-md rounded-3xl border border-line bg-surface px-8 py-16 text-center" data-testid="prime-store-empty">
            <Store className="mx-auto h-10 w-10 text-[#C89A4B]" />
            <h2 className="mt-4 font-display text-xl font-bold text-1">
              {q || category ? "Nothing matches those filters" : "The PRIME Store is being stocked"}
            </h2>
            <p className="mt-2 text-sm text-3">
              {q || category
                ? "Try a different search or category."
                : "Our team is hand-picking products with member-only prices. Check back soon — or browse the full catalogue in the meantime."}
            </p>
            <ShinyButton onClick={() => (q || category ? (setQ(""), setCategory("")) : navigate("/shop"))}
              className="mt-6 rounded-full" contentClassName="px-6 py-2.5 text-sm font-bold" data-testid="prime-store-empty-cta">
              {q || category ? "Clear filters" : "Browse all products"}
            </ShinyButton>
          </div>
        ) : (
          <>
            <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-3" data-testid="prime-store-count">
              Showing {products.length} of {total} product{total === 1 ? "" : "s"}
            </p>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} primeChannel />)}
            </div>
            {hasMore && (
              <div ref={loadMoreRef} className="mt-8 flex justify-center">
                <button
                  onClick={() => fetchProducts(page + 1, true)}
                  disabled={loadingMore}
                  data-testid="prime-store-load-more"
                  className="flex items-center gap-2 rounded-full border border-line bg-surface px-6 py-2.5 text-sm font-bold text-1 transition-colors hover:border-[#242521]/40 disabled:opacity-50"
                >
                  {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </section>
    </div>
  );
}
